<template>
  <div class="asset-drawer__form asset-drawer-tax-lots__toolbar">
    <UIPanel
      v-model="clearedSearch"
      v-model:storage="searchStorage"
      placeholder="Search tax lots..."
    >
      <template #default="{ ButtonExport }">
        <component
          :is="ButtonExport"
          :disabled="isExportDisabled"
          @click="handleClickExport"
        />
      </template>
    </UIPanel>
    <UIInputSelect
      v-model="statusFilter"
      :data="statusOptions"
      size="sm"
      class="asset-drawer-tax-lots__status"
    />
  </div>
  <UIGridSkeleton
    v-if="isLoading"
    :columns
    :sm
    message="Loading tax lots..."
  />
  <UIGrid
    v-else
    v-model:sort="sort"
    data-refid="assetTaxLotsList"
    :columns
    :items
    :sm
    :lazy="50"
    :collapse-width="520"
    scrollable
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import { AssetClass } from '..'
import { AssetTaxLot, Sort } from '@types'

import { ASSET_FIELD } from '../utils/const'

import {
  formatTaxLotCurrencyAmount,
  getTaxLotDisplayDate,
  getTaxLotGainLoss,
} from './utils/taxLots'
import { getAssetName } from '@/views/Analytics/utils/helpers'
import { handleCatchedError } from '@/helpers/common'
import { numberFormat } from '@/helpers/numbers'
import { stringToLocalDateString } from '@/helpers/dates'

import useGridFilter from '@/components/hooks/gridFilter'
import { useHandledGridExport } from '@/components/hooks/gridExport'
import useGridSort from '@/components/hooks/gridSort'

import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useAssetTaxLotsStore } from '@/store/assets/taxLots'

import { UIPanel } from '@ui/core'
import { UIInputSelect } from '@ui/fields'
import { UIGrid, UIGridSkeleton } from '@ui/grid'

type Props = {
  instance: AssetClass
}

const props = defineProps<Props>()

defineOptions({ inheritAttrs: false })

const assetsBunchStore = useAssetsBunchStore()
const taxLotsStore = useAssetTaxLotsStore()

const isLoading = ref(true)
const taxLots = ref<AssetTaxLot[]>([])

const searchStorage = ref<string>()
const clearedSearch = ref<string>()
const sort = ref<Sort[]>()
const statusFilter = ref<'all' | AssetTaxLot['status']>('all')

const statusOptions = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
]

const assetsBunch = computed(() => assetsBunchStore.getList)

const sm = '1fr 1fr'
const columns = computed(() => [
  {
    name: 'date',
    caption: 'Date',
    formatter: (_: string, item?: AssetTaxLot) =>
      stringToLocalDateString(item ? getTaxLotDisplayDate(item) : null),
    default: '110px',
  },
  {
    name: 'status',
    caption: 'Status',
    default: '80px',
  },
  {
    name: 'account_id',
    caption: 'Account',
    formatter: (accountId: string) =>
      getAssetName(assetsBunch.value, '')(accountId) || accountId,
    default: '180px',
  },
  {
    name: 'acquisition_date',
    caption: 'Acquired',
    formatter: stringToLocalDateString,
    default: '110px',
  },
  {
    name: 'units',
    caption: 'Units',
    formatter: (value: number) =>
      numberFormat(value, { fractionDigits: 0, fractionDigitsMax: 8 }),
    headerValueClasses: 'ui-grid__header-value--right',
    cellValueClasses: 'ui-grid__cell--right number-node',
    default: '110px',
  },
  {
    name: 'cost_basis',
    caption: 'Cost basis',
    formatter: (value: number, item?: AssetTaxLot) =>
      formatTaxLotCurrencyAmount(value, item?.currency || ''),
    headerValueClasses: 'ui-grid__header-value--right',
    cellValueClasses: 'ui-grid__cell--right number-node',
    default: '130px',
  },
  {
    name: 'gain_loss',
    caption: 'Gain/Loss',
    formatter: (_: number, item?: AssetTaxLot) =>
      item
        ? formatTaxLotCurrencyAmount(getTaxLotGainLoss(item), item.currency)
        : '',
    headerValueClasses: 'ui-grid__header-value--right',
    cellValueClasses: 'ui-grid__cell--right number-node',
    default: '130px',
  },
  {
    name: 'holding_period',
    caption: 'Term',
    default: '80px',
  },
  {
    name: 'provider',
    caption: 'Source',
    default: '80px',
  },
])

const inputItems = computed(() =>
  statusFilter.value === 'all'
    ? taxLots.value
    : taxLots.value.filter(item => item.status === statusFilter.value),
)
const filteredItems = useGridFilter(columns, inputItems, clearedSearch)
const items = useGridSort(sort, columns, filteredItems, ['units', 'cost_basis'])

const isExportDisabled = computed(() => items.value.length === 0)

const fetchTaxLots = async () => {
  try {
    taxLots.value = await taxLotsStore.fetch(props.instance.id)
  } catch (e) {
    handleCatchedError(e as string, { asset_id: props.instance.id })
  } finally {
    isLoading.value = false
  }
}

const handleClickExport = () => {
  const assetName = props.instance.field(ASSET_FIELD.NAME).value
  useHandledGridExport(columns.value, items.value, `${assetName}_tax_lots`, true)
}

onMounted(fetchTaxLots)
</script>

<style>
.asset-drawer-tax-lots {
  &__toolbar {
    @apply items-start;
  }

  &__status {
    @apply w-44 shrink-0;
  }
}
</style>
